import axios from "axios";
import React, { useEffect, useState } from "react";

const AddTermsConditionsModal = ({ isOpen, onClose, orderId, products }) => {
  const [data, setData] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false); // To show loading state
  const [isUserSelected, setIsUserSelected] = useState(false); // Track if user has manually selected an option

  const dataTermsConditions = products.termsConditions;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://server-gs.vercel.app/event-company-profile"
        );
        setData(response.data);
        setError(null); // Clear any previous errors

        // Only set the default option if the user hasn't manually selected an option
        if (!isUserSelected && response.data) {
          const matchedField = Object.keys(response.data).find(
            (key) => key === dataTermsConditions
          );

          // If a match is found, set the matched field as the default
          if (matchedField) {
            setSelectedOption(matchedField);
          }
        }
      } catch (err) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    // Fetch data every second
    const intervalId = setInterval(fetchData, 1000);

    // Cleanup the interval on component unmount
    return () => clearInterval(intervalId);
  }, [dataTermsConditions, isUserSelected]); // Add isUserSelected as a dependency

  // Handle dropdown change
  const handleSelectChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedOption(selectedValue);
    setIsUserSelected(true); // Mark that the user has manually selected an option
  };

  // Render the selected content
  const renderSelectedContent = () => {
    if (!data) return null;

    // If selectedOption matches a field in data, return its value
    if (selectedOption && data[selectedOption]) {
      return data[selectedOption];
    }

    // If dataTermsConditions matches a field in data, return its value as default
    if (dataTermsConditions && data[dataTermsConditions]) {
      return data[dataTermsConditions];
    }

    // Fallback if no match is found
    return "Please select an option.";
  };

  const handleAddTermsConditions = async () => {
    if (!selectedOption) {
      setError("Please select an option.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.put(
        `https://server-gs.vercel.app/admin/add-terms-conditions/${orderId}`,
        {
          selectedOption,
        }
      );

      if (response.status === 200) {
        onClose(); // Close the modal on success
      }
    } catch (err) {
      setError("Failed to update terms and conditions.");
      console.error("Error updating terms and conditions:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-[80%] md:w-[60%] h-[80%] md:h-[80%] shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex flex-row justify-between items-center px-6 py-4 bg-gradient-to-r bg-blue-600 rounded-t-2xl">
          <h2 className="text-xl font-bold font-roboto text-white">
            Add Terms and Conditions
          </h2>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="w-9 h-9 rounded-full flex justify-center items-center transition hover:bg-blue-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 flex flex-col md:flex-row gap-6 overflow-y-auto">
          {/* Left Side - Dropdown */}
          <div className="w-full md:w-1/3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select an Option
            </label>
            <select
              value={selectedOption}
              onChange={handleSelectChange}
              className="w-[30%] p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="">Select an option</option>
              <option value="termsConditionsOne">Terms Conditions One</option>
              <option value="termsConditionsTwo">Terms Conditions Two</option>
              <option value="termsConditionsThree">
                Terms Conditions Three
              </option>
              <option value="cancellationPolicy">Cancellation Policy</option>
              <option value="paymentPolicy">Payment Policy</option>
            </select>
          </div>

          {/* Right Side - Content */}
          <div className="w-full md:w-2/3">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Selected Content:
            </h2>
            <div
              className="prose prose-blue max-w-none"
              dangerouslySetInnerHTML={{ __html: renderSelectedContent() }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-start p-6 bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleAddTermsConditions}
            disabled={loading}
            className="w-56 py-3 font-roboto  bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300 transition-all"

>
            {loading ? "Updating..." : "Add Terms and Conditions"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTermsConditionsModal;